// 레이드 VFX 돌 조각(착지 충격파·돌진 끝): 로우폴리 현무암 조각 InstancedMesh 1(드로우콜 1).
// 튀어 올랐다가 중력으로 떨어져 바닥에서 한두 번 튀고, 마지막 0.35초에 작아지며 사라진다. 갓 튄 조각은 용암빛으로 달아 있다가 식는다.
// 풀(typed array) + 재사용 Matrix4·Quaternion·Euler만 쓴다(프레임 루프 할당 없음).
import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any
const { attribute, float, mix, normalView, vec3 } = TSL as unknown as Record<string, N>;

export const DEBRIS_CAP = 56;
const GRAVITY = -19;

export class RaidDebris {
  readonly mesh: THREE.InstancedMesh;
  readonly cap: number;
  scale = 1;
  private n = 0;
  private readonly px: Float32Array; private readonly py: Float32Array; private readonly pz: Float32Array;
  private readonly vx: Float32Array; private readonly vy: Float32Array; private readonly vz: Float32Array;
  private readonly rx: Float32Array; private readonly ry: Float32Array; private readonly rz: Float32Array;
  private readonly wx: Float32Array; private readonly wy: Float32Array; private readonly wz: Float32Array;
  private readonly age: Float32Array; private readonly life: Float32Array; private readonly size: Float32Array; private readonly heat0: Float32Array;
  private readonly aHeat: THREE.InstancedBufferAttribute;
  private rs = 0x3c6ef372;
  private readonly mtx = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler();
  private readonly p = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  spawned = 0;

  constructor(particleScale: number) {
    this.scale = Math.max(0.25, Math.min(1, particleScale));
    this.cap = Math.max(16, Math.floor(DEBRIS_CAP * this.scale));
    const geo = new THREE.IcosahedronGeometry(1, 0);
    // 조각답게 납작·비대칭으로
    geo.scale(1, 0.62, 0.8);
    this.aHeat = new THREE.InstancedBufferAttribute(new Float32Array(this.cap), 1);
    this.aHeat.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aHeat', this.aHeat);
    const mat = new THREE.MeshStandardNodeMaterial({ color: 0x2c2422, roughness: 0.92, metalness: 0 });
    mat.flatShading = true;
    // 달군 정도: 위를 향한 면은 덜, 옆·아래 면이 용암빛(갈라진 속)
    const heat: N = attribute('aHeat', 'float');
    const side = float(1).sub(normalView.y.abs().mul(0.6));
    mat.emissiveNode = mix(vec3(0, 0, 0), vec3(2.2, 0.55, 0.08), heat.mul(side));
    this.mesh = new THREE.InstancedMesh(geo, mat, this.cap);
    this.mesh.name = 'fx-raid-debris';
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.castShadow = false;
    const f = (): Float32Array => new Float32Array(this.cap);
    this.px = f(); this.py = f(); this.pz = f(); this.vx = f(); this.vy = f(); this.vz = f();
    this.rx = f(); this.ry = f(); this.rz = f(); this.wx = f(); this.wy = f(); this.wz = f();
    this.age = f(); this.life = f(); this.size = f(); this.heat0 = f();
  }

  get active(): number { return this.n; }
  private rnd(): number { this.rs = (Math.imul(this.rs, 1664525) + 1013904223) >>> 0; return this.rs / 4294967296; }

  /** (x,z) 둘레 반경 r에서 count개(품질 배율 적용)를 바깥·위로 튕긴다. speed = 바깥 속도(m/s), heat = 달군 정도 0..1 */
  burst(x: number, z: number, r: number, count: number, speed: number, up: number, size: number, heat: number): void {
    const k = Math.max(1, Math.round(count * this.scale));
    for (let c = 0; c < k; c++) {
      if (this.n >= this.cap) return;
      const i = this.n++;
      const a = this.rnd() * Math.PI * 2, rr = r * (0.3 + this.rnd() * 0.7), sp = speed * (0.5 + this.rnd() * 0.7);
      this.px[i] = x + Math.sin(a) * rr; this.py[i] = 0.2 + this.rnd() * 0.4; this.pz[i] = z + Math.cos(a) * rr;
      this.vx[i] = Math.sin(a) * sp; this.vz[i] = Math.cos(a) * sp; this.vy[i] = up * (0.55 + this.rnd() * 0.75);
      this.rx[i] = this.rnd() * 6; this.ry[i] = this.rnd() * 6; this.rz[i] = this.rnd() * 6;
      this.wx[i] = (this.rnd() - 0.5) * 14; this.wy[i] = (this.rnd() - 0.5) * 10; this.wz[i] = (this.rnd() - 0.5) * 14;
      this.age[i] = 0; this.life[i] = 1.3 + this.rnd() * 0.8; this.size[i] = size * (0.45 + this.rnd() * 0.8); this.heat0[i] = heat * (0.5 + this.rnd() * 0.5);
      this.spawned++;
    }
  }

  private kill(i: number): void {
    const j = --this.n;
    if (i === j) return;
    this.px[i] = this.px[j]; this.py[i] = this.py[j]; this.pz[i] = this.pz[j]; this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j]; this.vz[i] = this.vz[j];
    this.rx[i] = this.rx[j]; this.ry[i] = this.ry[j]; this.rz[i] = this.rz[j]; this.wx[i] = this.wx[j]; this.wy[i] = this.wy[j]; this.wz[i] = this.wz[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.size[i] = this.size[j]; this.heat0[i] = this.heat0[j];
  }

  update(dt: number): void {
    const h = this.aHeat.array as Float32Array;
    let i = 0;
    while (i < this.n) {
      if (dt > 0) {
        this.age[i] += dt;
        if (this.age[i] >= this.life[i]) { this.kill(i); continue; }
        this.vy[i] += GRAVITY * dt;
        this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt; this.pz[i] += this.vz[i] * dt;
        const floor = this.size[i] * 0.45;
        if (this.py[i] < floor) {
          this.py[i] = floor;
          if (this.vy[i] < -1.5) { this.vy[i] *= -0.32; this.vx[i] *= 0.55; this.vz[i] *= 0.55; this.wx[i] *= 0.5; this.wz[i] *= 0.5; }
          else { this.vy[i] = 0; this.vx[i] *= Math.max(0, 1 - 8 * dt); this.vz[i] *= Math.max(0, 1 - 8 * dt); this.wx[i] *= 0.9; this.wy[i] *= 0.9; this.wz[i] *= 0.9; }
        }
        this.rx[i] += this.wx[i] * dt; this.ry[i] += this.wy[i] * dt; this.rz[i] += this.wz[i] * dt;
      }
      const left = this.life[i] - this.age[i];
      const sc = this.size[i] * Math.min(1, left / 0.35);
      this.e.set(this.rx[i], this.ry[i], this.rz[i]);
      this.q.setFromEuler(this.e);
      this.p.set(this.px[i], this.py[i], this.pz[i]);
      this.s.set(sc, sc, sc);
      this.mtx.compose(this.p, this.q, this.s);
      this.mesh.setMatrixAt(i, this.mtx);
      h[i] = this.heat0[i] * Math.max(0, 1 - this.age[i] / 0.9);
      i++;
    }
    this.mesh.count = this.n;
    this.mesh.visible = this.n > 0;
    if (this.n > 0) { this.mesh.instanceMatrix.needsUpdate = true; this.aHeat.needsUpdate = true; }
  }

  prewarm(on: boolean): void { this.mesh.count = on ? Math.max(1, this.n) : this.n; this.mesh.visible = on || this.n > 0; }
  clear(): void { this.n = 0; this.mesh.count = 0; this.mesh.visible = false; }
  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}
